/**
 * CharacterSheet-aware derived stat helpers.
 *
 * These compose existing core primitives with CharacterSheet data so that
 * UI consumers don't need to reimplement the same formulas.
 */
import {
  ABILITY_NAMES,
  SKILL_NAMES,
  SKILL_ABILITY_MAP,
  type AbilityName,
  type SkillName,
  type CharacterSheet,
} from '@character-creator/types';
import { calculateSkillModifier, calculateSavingThrow } from './proficiency';

/**
 * Get skill modifier and proficiency level for a single skill.
 */
export function getSkillModifier(
  sheet: CharacterSheet,
  skill: SkillName,
): { modifier: number; proficiencyLevel: 'none' | 'proficient' | 'expertise' } {
  const ability = SKILL_ABILITY_MAP[skill];
  const abilityScore = sheet.finalScores[ability];
  const isProficient = sheet.skills.includes(skill);
  const hasExpertise = sheet.expertiseSkills.includes(skill);
  const proficiencyLevel = hasExpertise ? 'expertise' : isProficient ? 'proficient' : 'none';

  return {
    modifier: calculateSkillModifier(abilityScore, sheet.proficiencyBonus, proficiencyLevel),
    proficiencyLevel,
  };
}

/**
 * Get saving throw modifier and proficiency for a single ability.
 */
export function getSavingThrowModifier(
  sheet: CharacterSheet,
  ability: AbilityName,
): { modifier: number; isProficient: boolean } {
  const isProficient = sheet.savingThrows.includes(ability);
  return {
    modifier: calculateSavingThrow(sheet.finalScores[ability], sheet.proficiencyBonus, isProficient),
    isProficient,
  };
}

/**
 * Get passive score for a skill (10 + skill modifier).
 */
export function getPassiveScore(sheet: CharacterSheet, skill: SkillName): number {
  return 10 + getSkillModifier(sheet, skill).modifier;
}

/**
 * Get base (unarmored) armor class: 10 + DEX modifier.
 */
export function getArmorClass(sheet: CharacterSheet): number {
  return 10 + sheet.modifiers.dexterity;
}

/**
 * Get all skill modifiers as a record.
 */
export function getAllSkillModifiers(sheet: CharacterSheet): Record<SkillName, number> {
  const result = {} as Record<SkillName, number>;
  for (const skill of SKILL_NAMES) {
    result[skill] = getSkillModifier(sheet, skill).modifier;
  }
  return result;
}

/**
 * Get all saving throw modifiers as a record.
 */
export function getAllSavingThrowModifiers(sheet: CharacterSheet): Record<AbilityName, number> {
  const result = {} as Record<AbilityName, number>;
  for (const ability of ABILITY_NAMES) {
    result[ability] = getSavingThrowModifier(sheet, ability).modifier;
  }
  return result;
}
